import { useCallback } from 'react';
import styled from 'styled-components';
import {
  faCheck,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from 'components/Modal';
import Button from 'components/inputs/Button';
import WorkTimeWidget from 'pages/Employees/Availability/WorkTimeWidget';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { formatDate } from 'utils/date-helpers';
import toast from 'react-hot-toast';
import {
  useUserGetEmployeeAvailabilityForDate,
  useUserGetUserById,
  useUserUpdateUserAvailability,
} from 'api/user/user';
import { useQueryClient } from '@tanstack/react-query';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 900px;
  max-width: 1200px;
  max-height: 80vh;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const FullyBookedButton = styled(Button)<{ isBooked?: boolean }>`
  background-color: ${({ isBooked }) => (isBooked ? '#4CAF50' : undefined)};
  color: ${({ isBooked }) => (isBooked ? 'white' : undefined)};
`;

interface Props {
  userId: number;
  userName: string;
  date: Date;
  onClose: () => void;
}

const DayPlanningDetailModal: React.FC<Props> = ({
  userId,
  userName,
  date,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const { data: user, isLoading: userLoading } = useUserGetUserById(
    { userId },
    { query: { enabled: !!userId } }
  );

  const { data: availabilityData, isLoading: availLoading } =
    useUserGetEmployeeAvailabilityForDate(
      { userId, date },
      { query: { enabled: !!userId } }
    );

  const { mutateAsync: updateAvailability, isLoading: isUpdating } =
    useUserUpdateUserAvailability();

  const isFullyBooked = availabilityData?.isFullyBooked === true;

  const handleToggleFullyBooked = useCallback(async () => {
    if (!availabilityData) return;

    try {
      await updateAvailability({
        data: {
          userId,
          availability: {
            ...availabilityData,
            isFullyBooked: !isFullyBooked,
          },
        },
      });

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries([
        '/api/User/GetEmployeeAvailabilityForDate',
      ]);

      toast.success(
        !isFullyBooked
          ? `${userName} markerad som fullbokad`
          : `Fullbokad-markering borttagen för ${userName}`
      );
    } catch {
      toast.error('Kunde inte uppdatera tillgänglighet');
    }
  }, [
    availabilityData,
    isFullyBooked,
    updateAvailability,
    userId,
    userName,
    queryClient,
  ]);

  const loading = userLoading || availLoading;

  return (
    <Modal
      title={`Dagsplanering: ${userName} - ${formatDate(date)}`}
      closeOnOutsideClick
      buttons={[
        {
          label: 'Stäng',
          onClick: onClose,
        },
      ]}
    >
      <Content>
        <Header>
          <HeaderInfo>
            <h2>{userName}</h2>
            <span>
              Hemort:{' '}
              {user?.city ?? user?.zipCodeLocalArea?.name ?? 'okänd'}
              {user?.isAllowedHalfdayPlanning &&
                ', halvdagsplanering tillåten'}
            </span>
          </HeaderInfo>
          <ActionButtons>
            <FullyBookedButton
              isBooked={isFullyBooked}
              onClick={handleToggleFullyBooked}
              disabled={isUpdating || !availabilityData}
            >
              {isUpdating ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faCheck} />
              )}{' '}
              {isFullyBooked ? 'Fullbokad' : 'Markera som fullbokad'}
            </FullyBookedButton>
          </ActionButtons>
        </Header>

        {loading ? (
          <LoadingSpinner>Laddar dagsplanering...</LoadingSpinner>
        ) : user && availabilityData ? (
          <WorkTimeWidget availability={availabilityData} user={user} />
        ) : (
          <p>Kunde inte ladda data för fälttestaren.</p>
        )}
      </Content>
    </Modal>
  );
};

export default DayPlanningDetailModal;
